import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { checkAcceptedTerms } from "src/api/termsAndConditions";
import SuspenseLoader from "src/components/SuspenseLoader/SuspenseLoader";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { useDoLogout } from "src/utils/useDoLogout";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

import { isPlatform } from "@ionic/react";
import { isE2eAuthBypassEnabled } from "src/utils/e2eAuthBypass";

const skipAuthForTest = isE2eAuthBypassEnabled();

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const doLogOut = useDoLogout();
  const [isCheckingTerms, setIsCheckingTerms] = useState(true);

  const checkTerms = useCallback(async () => {
    try {
      // Check if we are returning from Yahoo
      const isReturningFromYahoo = window.location.href.includes("yahoo_callback") || window.location.href.includes("yahoo_token");

      // if (isReturningFromYahoo) {
      //   // If we are returning from Yahoo, do not check terms
      //   setIsChecking(false);
      //   return;
      // }
      const accessToken = await getSafeAccessAuth0Token();

      if (!accessToken) {
        // Logout is in progress, stop execution.
        return;
      }

      const hasAcceptedTerms = await checkAcceptedTerms(accessToken);

      if (!hasAcceptedTerms) {
        navigate("/terms-and-conditions", { replace: true });
        return;
      }
      setIsCheckingTerms(false);
    } catch (error) {
      // This catch is for errors from checkAcceptedTerms, not token acquisition.
      console.error("Error checking terms:", error);
      // Account was deleted elsewhere / session rejected by backend → logout → /get-started
      const status = (error as { status?: number })?.status;
      if (status === 404 || status === 401) {
        await doLogOut();
        return;
      }
      // Optionally navigate to an error page or show a message.
      // For now, we keep the previous behavior.
      navigate("/terms-and-conditions", { replace: true });
    }
  }, [getSafeAccessAuth0Token, navigate, doLogOut]);

  useEffect(() => {
    // skip Auth0 and terms check for testing against local POC
    if (skipAuthForTest) {
      setIsCheckingTerms(false);
      return;
    }

    if (isLoading) {
      return; // Wait for Auth0 SDK to be ready
    }

    // ProtectedRoute is ONLY used for /home route which requires Auth0 authentication
    // If not authenticated, redirect to get-started
    // Note: After handleRedirectCallback in App.tsx, isAuthenticated may take a moment to update
    // So we check isLoading first to ensure Auth0 has finished processing the callback
    if (!isAuthenticated) {
      navigate("/get-started", { replace: true });
      return;
    }

    // If authenticated, check terms
    if (isAuthenticated) {
      checkTerms();
    }
  }, [isLoading, isAuthenticated, checkTerms, navigate]);

  // Account deleted / session invalid: interceptor (or resume check) emits this → logout
  useEffect(() => {
    const onAccountDeleted = () => {
      void doLogOut();
    };
    window.addEventListener("account-deleted", onAccountDeleted);
    return () => window.removeEventListener("account-deleted", onAccountDeleted);
  }, [doLogOut]);

  // Re-validate on foreground return (fire-and-forget: no loader, no UX delay).
  // Use getAccessTokenSilently directly (NOT getSafeAccessAuth0Token) so a token hiccup
  // on resume never auto-logouts and leaves credits unloaded → false ManageMembership paywall.
  // Backend 401 (deleted account) is handled by the global fetch interceptor → account-deleted.
  // Keep 404/401 local handling as a backup if the interceptor did not run.
  useEffect(() => {
    if (skipAuthForTest || !isAuthenticated) return;

    const revalidateSessionOnResume = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        if (!accessToken) return;
        await checkAcceptedTerms(accessToken);
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 404 || status === 401) {
          await doLogOut();
        }
        // Ignore other errors on resume (network/token blips) — don't block UX
      }
    };

    // Check if we are on a native platform
    if (Capacitor.isNativePlatform()) {
      let cancelled = false;
      let nativeListener: PluginListenerHandle | undefined;
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) void revalidateSessionOnResume();
      }).then((l) => {
        if (cancelled) {
          // Effect cleaned up before listener resolved — remove immediately to avoid leak
          void l.remove();
          return;
        }
        nativeListener = l;
      });
      return () => {
        cancelled = true;
        nativeListener?.remove();
      };
    }

    // Check if we are on a web platform
    const onVis = () => {
      if (document.visibilityState === "visible") void revalidateSessionOnResume();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isAuthenticated, getAccessTokenSilently, doLogOut]);

  if (skipAuthForTest) {
    return <>{children}</>;
  }

  if (isLoading || isCheckingTerms) {
    return <SuspenseLoader />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
